/**
 * The running order.
 *
 * Shows the subtotal it can work out itself, and the amount owed as the server
 * calculates it. Only the second is what the customer pays: tax is applied
 * from the branch's rules and comes to about 16%, which is far too much to
 * leave a cashier to spot.
 *
 * While the server is being asked, the previous total stays on screen dimmed
 * rather than vanishing — a number that flickers away on every tap is worse
 * than one briefly a moment behind — and Charge is disabled until it settles.
 */
import { formatPKR } from '@foodrun/shared';

import { useQuery } from '@tanstack/react-query';

import { api, withQuery } from '../../lib/api.js';
import { useCart, lineTotal } from './cart.js';
import { TablePicker } from './TablePicker.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { KnownCustomer } from './KnownCustomer.jsx';
import { ManagerApproval } from './ManagerApproval.jsx';
import { needsApproval } from './approvalCodes.js';

const ORDER_TYPES = [
  { value: 'dine_in', label: 'Dine in' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'foodpanda', label: 'Foodpanda' },
];

export function CartPanel({
  onCharge,
  onPayLater,
  onFoodpanda,
  addingTo,
  onSaveEdit,
  onCancelEdit,
  editError,
  onSendEditRequest,
  sendingEdit,
  placing,
  quote,
  quoting,
  quoteError,
  onPark,
  onShowHeld,
  onDiscount,
  heldCount = 0,
  onShowTables,
  parking,
}) {
  const lines = useCart((s) => s.lines);
  const orderType = useCart((s) => s.orderType);
  const setOrderType = useCart((s) => s.setOrderType);
  const increment = useCart((s) => s.increment);
  const decrement = useCart((s) => s.decrement);
  const remove = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);
  const subtotal = useCart((s) => s.subtotal());
  const count = useCart((s) => s.count());
  const deliveryZoneId = useCart((s) => s.deliveryZoneId);
  const tableId = useCart((s) => s.tableId);
  const tableNumber = useCart((s) => s.tableNumber);

  /**
   * What is inside each deal, so a deal line can list its offerings under the
   * one price. Shares the menu the product grid already loaded — same query
   * key, so this reads from the cache rather than fetching again.
   */
  const menu = useQuery({
    queryKey: ['menu', 'products'],
    queryFn: () =>
      api.get(withQuery('/menu/products', { limit: 500, is_active: true, with_modifiers: 1 })),
    staleTime: 5 * 60_000,
  });
  // Names for the products chosen inside a deal, to list them under the line.
  const nameById = {};
  for (const p of menu.data?.products ?? []) nameById[p.id] = p.name;

  /**
   * A dine-in order with nobody sitting anywhere.
   *
   * The branch refuses this, so the only question is where the cashier finds
   * out. Left to the server it arrives after they have chosen a payment method
   * and pressed Complete, with a customer waiting. Here it is a disabled
   * button and a line saying which button to press instead.
   */
  const needsATable = orderType === 'dine_in' && !tableId && !tableNumber;
  const setDeliveryZone = useCart((s) => s.setDeliveryZone);
  const deliveryFee = useCart((s) => s.deliveryFee);
  const setDeliveryFee = useCart((s) => s.setDeliveryFee);
  const useKnownCustomer = useCart((s) => s.useKnownCustomer);
  const customerName = useCart((s) => s.customerName);
  const customerPhone = useCart((s) => s.customerPhone);
  const deliveryAddress = useCart((s) => s.deliveryAddress);
  const externalOrderId = useCart((s) => s.externalOrderId);
  const notes = useCart((s) => s.notes);
  const setField = useCart((s) => s.setField);

  // Only fetched when it is needed. A branch that does not deliver never asks.
  const zones = useQuery({
    queryKey: ['delivery', 'zones'],
    queryFn: () => api.get('/delivery/zones'),
    enabled: orderType === 'delivery',
    staleTime: 10 * 60_000,
  });

  const zoneList = zones.data?.zones ?? [];

  return (
    <aside className="flex h-full w-full flex-col border-l border-rule bg-surface">
      <div className="border-b border-rule p-3">
        {addingTo ? (
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-ink-3">Editing</p>
              <p className="truncate font-semibold text-ink">{addingTo.label}</p>
            </div>
            {/* Backing out discards the changes and returns to a normal order.
                Goes through onCancelEdit so a pending approval prompt is cleared
                too — otherwise the PIN box lingers onto the next order. */}
            <Button variant="secondary" onClick={onCancelEdit ?? clear} disabled={placing}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="mb-2.5 flex items-center gap-1.5">
          <div className="flex gap-1.5">
            <button
              onClick={onPark}
              disabled={lines.length === 0 || parking}
              className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface-3 disabled:opacity-40"
              title="Put this order aside and serve someone else"
            >
              Park
            </button>

            {onShowTables && (
              <button
                onClick={onShowTables}
                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface-3"
                title="Settle a bill a waiter took at a table"
              >
                Tables
              </button>
            )}

            {heldCount > 0 && (
              <button
                onClick={onShowHeld}
                className="rounded-lg bg-brand-400/15 px-2.5 py-1.5 text-xs font-medium text-brand-300 hover:bg-brand-400/25"
              >
                {heldCount} parked
              </button>
            )}
          </div>
        </div>
        )}

        <div role="group" aria-label="Order type" className="flex gap-1.5">
          {ORDER_TYPES.map((type) => (
            <button
              key={type.value}
              aria-pressed={orderType === type.value}
              onClick={() => setOrderType(type.value)}
              className={`h-10 flex-1 rounded-lg text-sm font-medium transition-colors ${
                orderType === type.value
                  ? 'bg-brand-400 text-surface-2'
                  : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/*
          Who the order is for. A delivery needs a name, a number and a door; a
          takeaway takes the name and number too, but optionally — enough to put
          on the bill and on a regular's record. The address is delivery only.
        */}
        {(orderType === 'delivery' || orderType === 'takeaway') && (
          <div className="mt-2 space-y-2">
            {/*
              Who the rider is handing it to, and the first thing anybody asks.
              The field was missing entirely: the cart carried a name and sent
              one, and nothing on the screen ever set it — so every delivery
              reached the dispatch board as "No name", and the rider knocked on
              a door with an address and a phone number and nobody to ask for.
            */}
            <label className="block">
              <span className="sr-only">Customer name</span>
              <input
                aria-label="Customer name"
                value={customerName}
                onChange={(e) => setField('customerName', e.target.value)}
                placeholder="Name"
                autoComplete="off"
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
            </label>

            <label className="block">
              <span className="sr-only">Customer phone</span>
              <input
                aria-label="Customer phone"
                value={customerPhone}
                onChange={(e) => setField('customerPhone', e.target.value)}
                placeholder="Phone number"
                inputMode="tel"
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
            </label>

            {/* Only a delivery goes to an address. */}
            {orderType === 'delivery' && (
              <label className="block">
                <span className="sr-only">Delivery address</span>
                <input
                  aria-label="Delivery address"
                  value={deliveryAddress}
                  onChange={(e) => setField('deliveryAddress', e.target.value)}
                  placeholder="Address"
                  className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
                />
              </label>
            )}

            {/*
              Below the fields, not over them. A cashier is typing what they can
              hear on a phone call, and a list covering the box being typed into
              is a list in the way.
            */}
            <KnownCustomer name={customerName} phone={customerPhone} onPick={useKnownCustomer} />
          </div>
        )}

        {/* A Foodpanda order carries the app's own order number, keyed in so the
            bill and the Foodpanda tab can be matched back to it. */}
        {orderType === 'foodpanda' && (
          <label className="mt-2 block">
            <span className="mb-1 block text-xs font-medium text-ink-2">Foodpanda order #</span>
            <input
              aria-label="Foodpanda order number"
              value={externalOrderId}
              onChange={(e) => setField('externalOrderId', e.target.value)}
              placeholder="e.g. 8f2a-1194"
              autoComplete="off"
              className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          </label>
        )}

        {orderType === 'delivery' && zoneList.length > 0 && (
          <label className="mt-2 block">
            <span className="sr-only">Delivery zone</span>
            <select
              aria-label="Delivery zone"
              value={deliveryZoneId ?? ''}
              onChange={(e) => setDeliveryZone(e.target.value || null)}
              className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            >
              <option value="">Which area?</option>
              {zoneList.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name} · {formatPKR(zone.delivery_fee)}
                  {zone.minimum_order > 0 ? ` · min ${formatPKR(zone.minimum_order)}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {/*
          The delivery fee, editable. It shows the fee that would be charged —
          the zone's or the flat default — as a placeholder, and a cashier can
          type a different one for this trip: a longer run, a regular waved
          through, a round figure agreed on the phone. Left blank, the usual fee
          stands.
        */}
        {orderType === 'delivery' && (
          <label className="mt-2 flex items-center gap-2">
            <span className="shrink-0 text-sm text-ink-2">Delivery fee</span>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-3">
                Rs.
              </span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                aria-label="Delivery fee"
                value={deliveryFee ?? ''}
                onChange={(e) => setDeliveryFee(e.target.value)}
                placeholder={String(quote?.deliveryFee ?? 100)}
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 pl-10 pr-3 text-right text-sm tabular-nums text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
            </div>
          </label>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {lines.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-3">
            Tap an item to start the order.
          </p>
        ) : (
          <ul className="divide-y divide-rule">
            {lines.map((line) => (
              <li key={line.key} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug text-ink">{line.name}</p>
                    {line.modifiers?.length > 0 && (
                      <p className="mt-0.5 text-xs text-ink-2">
                        {line.modifiers.map((m) => m.name).join(', ')}
                      </p>
                    )}
                    {/* A deal lists what the customer chose to fill it, under
                        the one price, so the cashier and the customer can see
                        exactly what the deal is. */}
                    {line.components?.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {line.components.map((chosen, i) => (
                          <li key={i} className="text-xs text-ink-2">
                            <span className="tabular-nums text-ink-3">{chosen.quantity}×</span>{' '}
                            {nameById[chosen.productId] ?? chosen.name ?? 'Item'}
                            {chosen.modifierNames?.length > 0 && (
                              <span className="text-ink-3"> · {chosen.modifierNames.join(', ')}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">
                    {formatPKR(lineTotal(line))}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <QtyButton onClick={() => decrement(line.key)} label={`One fewer ${line.name}`}>
                    −
                  </QtyButton>
                  <span className="w-8 text-center text-sm font-semibold tabular-nums">
                    {line.quantity}
                  </span>
                  <QtyButton onClick={() => increment(line.key)} label={`One more ${line.name}`}>
                    +
                  </QtyButton>

                  <button
                    onClick={() => remove(line.key)}
                    className="ml-auto rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface-3"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/*
          One note for the whole order, in the customer's words, printed as-is
          under the ticket in the kitchen — "cut the burger in half", "don't
          slice the pizza". Not per-dish; the kitchen reads it once, under the
          order. Only shown once there is an order to attach it to.
        */}
        {lines.length > 0 && (
          <div className="border-t border-rule p-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-2">
                Instructions for the kitchen
              </span>
              <textarea
                aria-label="Order instructions"
                value={notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="e.g. cut the burger in half, don't slice the pizza"
                rows={2}
                className="w-full resize-none rounded-lg border border-rule bg-surface-3 px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
            </label>
          </div>
        )}
      </div>

      <TablePicker />

      <div className="border-t border-rule p-3">
        <dl className="space-y-1 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-ink-2">Subtotal</dt>
            <dd className="tabular-nums text-ink-2">{formatPKR(subtotal)}</dd>
          </div>

          {/*
            What came off, and by which route. An offer and a typed-in
            discount are shown apart because they are different things — one
            was decided by head office, one by whoever is standing here.
          */}
          {quote?.promotionDiscount > 0 && (
            <div className="flex items-baseline justify-between">
              <dt className="text-brand-400">{quote.promotion?.name ?? 'Offer'}</dt>
              <dd className="tabular-nums text-brand-400">
                &minus;{formatPKR(quote.promotionDiscount)}
              </dd>
            </div>
          )}

          {quote?.discountAmount > 0 && (
            <div className="flex items-baseline justify-between">
              <dt className="text-ink-2">Discount</dt>
              <dd className="tabular-nums text-ink-2">&minus;{formatPKR(quote.discountAmount)}</dd>
            </div>
          )}

          {/*
            What the tax is being charged on, shown only where a discount has
            moved it away from the subtotal.

            On an undiscounted bill it would just repeat the subtotal and add a
            line to read past. On a discounted one it is the number the cashier
            needs when a customer asks why the tax is not a round percentage of
            what they see at the top — and it is the line whose absence let the
            till charge tax on the pre-discount figure for 116 orders without
            anybody noticing.
          */}
          {quote?.totalDiscount > 0 && quote?.taxableAmount !== undefined && (
            <div className="flex items-baseline justify-between border-t border-rule/60 pt-1.5">
              <dt className="text-ink-2">Taxable amount</dt>
              <dd className="tabular-nums text-ink-2">{formatPKR(quote.taxableAmount)}</dd>
            </div>
          )}

          {quote?.taxAmount > 0 && (
            <div className="flex items-baseline justify-between">
              <dt className="text-ink-2">
                Tax{quote?.taxRate ? ` (${quote.taxRate}%)` : ''}
              </dt>
              <dd className="tabular-nums text-ink-2">{formatPKR(quote.taxAmount)}</dd>
            </div>
          )}

          {quote?.deliveryFee > 0 && (
            <div className="flex items-baseline justify-between">
              <dt className="text-ink-2">Delivery</dt>
              <dd className="tabular-nums text-ink-2">{formatPKR(quote.deliveryFee)}</dd>
            </div>
          )}

          <div className="flex items-baseline justify-between border-t border-rule pt-1.5">
            <dt className="font-medium text-ink">Total</dt>
            <dd
              aria-live="polite"
              className={`text-xl font-semibold tabular-nums text-ink ${quoting ? 'opacity-50' : ''}`}
            >
              {quote ? formatPKR(quote.totalAmount) : lines.length ? '—' : formatPKR(0)}
            </dd>
          </div>
        </dl>

        {quoteError && (
          <p role="alert" className="mt-2 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">
            {quoteError.message}
            {/* A minimum-order refusal is something the cashier can fix by
                adding an item. Everything else is a fault. */}
            {quoteError.code !== 'BELOW_MINIMUM' &&
              ' You cannot take payment until this is working.'}
          </p>
        )}

        {needsATable && (
          <p className="mt-3 text-sm text-warn">
            Pick a table for a dine-in order, or switch to Takeaway.
          </p>
        )}

        {/*
          A manager's PIN, when the branch requires one to change an order. The
          server refuses the save with an approval code; the box opens in place
          and the same edit is sent again with the PIN attached, so nothing the
          cashier changed is lost.
        */}
        {addingTo && needsApproval(editError) && (
          <>
            <ManagerApproval
              error={editError}
              busy={placing}
              onApprove={(approval) => onSaveEdit(approval)}
            />
            {/* Or send the edit up for a manager to approve on their own device.
                Nothing changes now; the order waits on the board until they do. */}
            {onSendEditRequest && (
              <button
                type="button"
                onClick={onSendEditRequest}
                disabled={sendingEdit}
                className="mt-2 w-full rounded-lg border border-rule px-3 py-2 text-sm font-medium text-ink-2 hover:bg-surface-3 disabled:opacity-40"
              >
                {sendingEdit ? 'Sending…' : 'Or send to a manager to approve'}
              </button>
            )}
          </>
        )}

        {addingTo && !needsApproval(editError) && (
          <Button
            size="lg"
            onClick={() => onSaveEdit()}
            busy={placing}
            // A total the server has confirmed, something on the bill, and — for
            // a dine-in order — a table for it to sit on.
            disabled={lines.length === 0 || !quote || quoting || needsATable}
            className="mt-3 w-full"
          >
            Save changes {count > 0 && `· ${count} item${count === 1 ? '' : 's'}`}
          </Button>
        )}

        {addingTo && editError && !needsApproval(editError) && (
          <p role="alert" className="mt-2 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">
            {editError.message}
          </p>
        )}

        {/*
          Two rows, because four buttons do not fit across this panel.

          They were on one line, and adding a fourth pushed the total past the
          panel's width: Charge was clipped at the edge with its own label
          wrapped onto two lines. Tidying it as one row of four smaller buttons
          would have been the wrong repair — this is a touchscreen used at speed,
          and the two that finish an order are the last thing that should get
          smaller.

          So the housekeeping goes on its own line, and the ways to finish get a
          full-width row to share.

          All of it hidden while adding to an existing bill — there is nothing
          to charge or park here, only items to add, above.
        */}
        {!addingTo && (
          <>
        <div className="mt-3 flex gap-2">
          <Button
            variant="secondary"
            onClick={clear}
            disabled={lines.length === 0 || placing}
            className="flex-1"
          >
            Clear
          </Button>
          {onDiscount && (
            <Button variant="secondary" onClick={onDiscount} className="flex-1">
              Offer
            </Button>
          )}
        </div>

        <div className="mt-2 flex gap-2">
          {orderType === 'foodpanda' ? (
            /*
              A Foodpanda order takes no money at the counter — the customer
              paid Foodpanda, which pays the shop out weeks later. So there is
              one button, and it places the order as owed by Foodpanda; it shows
              up on the Foodpanda tab to be cleared when the payout lands.
            */
            <Button
              size="lg"
              onClick={onFoodpanda}
              busy={placing}
              disabled={lines.length === 0 || !quote || quoting}
              className="flex-1"
            >
              Place Foodpanda order {count > 0 && `· ${count} item${count === 1 ? '' : 's'}`}
            </Button>
          ) : (
            <>
              {/*
                An order that goes to the kitchen before it is paid for — a
                dine-in customer who settles after eating, or a takeaway rung up
                to be paid on collection or put on a regular's tab. The bill sits
                unpaid on the board and is settled later with "Take payment"; no
                money, no journal entry, until then — exactly a waiter's order.

                Not offered on a delivery, which has its own way of recording what
                is owed and telling the rider to collect it.
              */}
              {onPayLater && (orderType === 'dine_in' || orderType === 'takeaway') && (
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={onPayLater}
                  busy={placing}
                  // The same conditions as Charge, minus the money: a total the
                  // server has confirmed, and a table for the bill to sit on.
                  disabled={lines.length === 0 || !quote || quoting || needsATable}
                  className="flex-1"
                >
                  Pay later
                </Button>
              )}

              <Button
                size="lg"
                onClick={onCharge}
                busy={placing}
                // No charging against a total the server has not confirmed. That
                // is the whole reason this screen asks it in the first place.
                // And no dine-in order without a table: the branch refuses it, so
                // stopping here says why before the cashier has typed a payment.
                disabled={lines.length === 0 || !quote || quoting || needsATable}
                className="flex-1"
              >
                Charge {count > 0 && `· ${count} item${count === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </div>
        </>
        )}
      </div>
    </aside>
  );
}

function QtyButton({ label, children, ...props }) {
  return (
    <button
      aria-label={label}
      className="size-11 rounded-lg border border-rule text-lg font-medium text-ink-2 hover:bg-surface-3 active:bg-rule"
      {...props}
    >
      {children}
    </button>
  );
}
